import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ContentBox from '../../components/ContentBox';
import {
    FrankoPopLayout, FpModal, FpButton, BoxArt, FigureFrame, RarityPill, StatusPill, EmptyState, ActivityRow,
} from '../../components/frankopop/frankopopShared';
import {
    useFrankopopData, fetchSets, fetchWallet, fetchActivity,
    purchaseBox, openBox, CURRENCY, SCRAP_UNIT,
} from '../../data/frankopop';

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function HowItWorks() {
    const steps = [
        ['Buy a box', 'Pick a set and grab a blind box with framp cards. You never know which FrankoPop is inside.'],
        ['Open it', 'Crack the box for the reveal - the odds are set per FrankoPop, per set.'],
        ['Keep or sell', 'Display it, keep it sealed in its case, or sell duplicates back for framps.'],
        ['Complete the set', 'Some FrankoPops are limited. Some sets are staff-gift only and never appear here.'],
    ];
    return (
        <ContentBox theme="default" tint="mediumpurple" header="How FrankoPop works">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {steps.map(([title, body], i) => (
                    <div key={title} className="bg-white border border-gray-300 p-3" style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.12)' }}>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="flex items-center justify-center w-6 h-6 bg-maroon text-white font-['Jersey_10'] text-[15px]" style={{ boxShadow: '0 2px 0 rgba(0,0,0,0.3)' }}>{i + 1}</span>
                            <h3 className="font-['Jersey_10'] uppercase text-[15px] tracking-[1px] text-gray-900">{title}</h3>
                        </div>
                        <p className="text-xs text-gray-500 leading-snug">{body}</p>
                    </div>
                ))}
            </div>
        </ContentBox>
    );
}

function ShopBoxCard({ set, wallet, onBuy }) {
    const soldOut = set.status === 'sold-out';
    const notYet = set.status === 'scheduled';
    const ended = set.status === 'ended';
    const cantAfford = wallet != null && wallet < set.priceCards;
    const buyable = set.status === 'on-sale' && !cantAfford;

    return (
        <div className="bg-white border border-gray-300 flex flex-col" style={{ boxShadow: '0 3px 0 0 rgba(0,0,0,0.15)' }}>
            <div className="bg-gray-100 border-b border-gray-200 flex items-center justify-center p-4">
                <BoxArt set={set} size={140} />
            </div>
            <div className="flex flex-col flex-1 p-3 gap-2">
                <div>
                    <h3 className="font-['Jersey_10'] uppercase text-[16px] tracking-[1px] text-gray-900 leading-tight">{set.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                        <StatusPill status={set.status} />
                        <span className="text-[11px] text-gray-400 font-['Jersey_10'] uppercase tracking-[1px]">{set.completableFigureCount} to collect</span>
                    </div>
                </div>

                <div className="flex items-end justify-between pt-1 border-t border-gray-200 mt-1">
                    <div>
                        <p className="font-['Jersey_10'] uppercase text-[11px] tracking-[1px] text-gray-400 leading-none">Price</p>
                        <p className="font-['Jersey_10'] text-[22px] leading-tight text-maroon">{set.priceCards} <span className="text-[13px] text-gray-400">{CURRENCY}</span></p>
                    </div>
                    <p className="text-[11px] text-gray-400 font-['Jersey_10'] uppercase tracking-[1px] text-right">
                        {set.limitedRun
                            ? `${set.boxesRemaining} / ${set.limitedRunTotal} left`
                            : notYet ? 'Not on sale yet' : ended ? 'Sale ended' : 'Unlimited'}
                    </p>
                </div>

                <FpButton
                    variant={buyable ? 'primary' : 'secondary'}
                    className="w-full mt-1"
                    disabled={!buyable}
                    onClick={() => onBuy(set)}
                >
                    {soldOut ? 'Sold out' : notYet ? 'Coming soon' : ended ? 'Sale ended' : cantAfford ? 'Not enough framp cards' : 'Buy box'}
                </FpButton>
            </div>
        </div>
    );
}

function GiftOnlyCard({ set }) {
    return (
        <div className="bg-white/60 border border-gray-300 border-dashed flex items-center gap-3 p-3">
            <BoxArt set={set} size={72} />
            <div className="flex-1">
                <h3 className="font-['Jersey_10'] uppercase text-[15px] tracking-[1px] text-gray-700">{set.name}</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                    Staff-gift only - never sold in the shop. Awarded by staff{set.giftPermission === 'admins_only' ? ' (admins only)' : ''}.
                </p>
            </div>
            <StatusPill status="gift-only" />
        </div>
    );
}

const fetchRecentActivity = async () => (await fetchActivity(1, 5)).events;

function RecentActivity({ events, loading }) {
    return (
        <ContentBox theme="default" tint="steelblue" header="Recent activity">
            {loading && !events ? (
                <p className="text-sm text-gray-500">Loading activity...</p>
            ) : !events || events.length === 0 ? (
                <p className="text-sm text-gray-500">Nobody has opened a FrankoPop yet - be the first!</p>
            ) : (
                <div className="space-y-2">
                    {events.map((event) => <ActivityRow key={event.id} event={event} />)}
                </div>
            )}
            <div className="flex justify-end mt-3">
                <Link to="/frankopop/activity" className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] text-maroon hover:underline">
                    View all activity &rarr;
                </Link>
            </div>
        </ContentBox>
    );
}

export default function FrankoPopShop({ userData }) {
    const { data: sets, loading, refetch: refetchSets } = useFrankopopData(fetchSets, []);

    const walletFetch = useCallback(() => (userData ? fetchWallet() : Promise.resolve(null)), [userData]);
    const { data: walletData, refetch: refetchWallet } = useFrankopopData(walletFetch, null);
    const { data: activity, loading: activityLoading, refetch: refetchActivity } = useFrankopopData(fetchRecentActivity, null);
    const wallet = walletData ? walletData.cards : null;

    const shopSets = (sets || []).filter((s) => !s.giftableOnly);
    const giftOnlySets = (sets || []).filter((s) => s.giftableOnly);

    const [confirmSet, setConfirmSet] = useState(null);
    const [opening, setOpening] = useState(false);
    const [reveal, setReveal] = useState(null);

    const startBuy = (set) => {
        if (!userData) { toast.info('Log in to buy FrankoPop boxes.'); return; }
        setConfirmSet(set);
    };

    const confirmBuy = async () => {
        const set = confirmSet;
        setConfirmSet(null);
        const res = await purchaseBox(set.id);
        if (!res.success) { toast.error(res.message); return; }

        setOpening(true);
        const [opened] = await Promise.all([openBox(res.boxId), wait(700)]);
        setOpening(false);
        if (!opened.success) { toast.error(opened.message); return; }
        setReveal({ figure: opened.figure, set });
        if (opened.scrapsAwarded > 0) toast.success(`Bonus! +${opened.scrapsAwarded} ${SCRAP_UNIT}!`);
        refetchSets();
        refetchWallet();
        refetchActivity();
    };

    return (
        <FrankoPopLayout active="shop" wallet={wallet}>
            <HowItWorks />

            <ContentBox theme="default" tint="indianred" header="Shop - blind boxes">
                {loading ? (
                    <p className="text-sm text-gray-500">Loading sets...</p>
                ) : shopSets.length === 0 ? (
                    <EmptyState title="No sets on sale" sub="Check back when the next set is released." />
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {shopSets.map((set) => <ShopBoxCard key={set.id} set={set} wallet={wallet} onBuy={startBuy} />)}
                    </div>
                )}
            </ContentBox>

            {giftOnlySets.length > 0 && (
                <ContentBox theme="default" tint="goldenrod" header="Not in the shop">
                    <p className="text-sm text-gray-600 mb-3">
                        These sets can&apos;t be bought - boxes are handed out by staff through events and rewards.
                    </p>
                    <div className="space-y-2">
                        {giftOnlySets.map((set) => <GiftOnlyCard key={set.id} set={set} />)}
                    </div>
                </ContentBox>
            )}

            <RecentActivity events={activity} loading={activityLoading} />

            {confirmSet && (() => {
                const cantAffordNow = wallet == null || wallet < confirmSet.priceCards;
                return (
                    <FpModal title="Buy this box?" onClose={() => setConfirmSet(null)}>
                        <div className="flex items-center gap-4 mb-4">
                            <BoxArt set={confirmSet} size={96} />
                            <div>
                                <p className="font-['Jersey_10'] uppercase text-[17px] tracking-[1px] text-gray-900">{confirmSet.name}</p>
                                <p className="text-sm text-gray-500">{confirmSet.figureCount} FrankoPops in this set.</p>
                            </div>
                        </div>
                        <div className={`border p-3 mb-4 text-sm ${cantAffordNow ? 'bg-red-50 border-red-400 text-red-800' : 'bg-yellow-50 border-yellow-400 text-yellow-800'}`}>
                            <p><strong>Cost:</strong> {confirmSet.priceCards} {CURRENCY}</p>
                            {cantAffordNow ? (
                                <p>You only have {wallet ?? 0} {CURRENCY} - not enough to buy this.</p>
                            ) : (
                                <p><strong>Balance after:</strong> {wallet - confirmSet.priceCards} {CURRENCY}</p>
                            )}
                        </div>
                        <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
                            <FpButton variant="secondary" onClick={() => setConfirmSet(null)}>Cancel</FpButton>
                            <FpButton variant="primary" onClick={confirmBuy} disabled={cantAffordNow}>
                                {cantAffordNow ? 'Not enough framp cards' : 'Buy & open'}
                            </FpButton>
                        </div>
                    </FpModal>
                );
            })()}

            {opening && (
                <FpModal onClose={() => {}} maxWidth="max-w-xs" zIndex={210}>
                    <div className="flex flex-col items-center gap-4 py-4">
                        <img src="https://api.franklyorigins.net/uploads/1789459777160-franko_crate.png" alt="" className="w-24 h-24 object-contain" style={{ imageRendering: 'pixelated', animation: 'fp-shake 0.4s ease-in-out infinite' }} />
                        <p className="font-['Jersey_10'] uppercase text-[18px] tracking-[1px] text-maroon">Opening box…</p>
                    </div>
                    <style>{`@keyframes fp-shake{0%,100%{transform:rotate(-6deg)}50%{transform:rotate(6deg)}}`}</style>
                </FpModal>
            )}

            {reveal && (
                <FpModal title="You pulled…" onClose={() => setReveal(null)} maxWidth="max-w-xs" zIndex={210}>
                    <div className="flex flex-col items-center gap-3">
                        <FigureFrame figure={reveal.figure} size={190} sealed />
                        <div className="text-center">
                            <p className="font-['Jersey_10'] uppercase text-[20px] tracking-[1px] text-coal_dark leading-tight">
                                {reveal.figure.name}
                            </p>
                            <RarityPill name={reveal.figure.rarityName} colour={reveal.figure.rarityColour} className="mt-1" />
                            <p className="text-xs text-gray-500 mt-2">{reveal.set?.name}</p>
                            {reveal.figure.ltd && (
                                <p className="font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-maroon mt-2">
                                    Limited - #{reveal.figure.awarded} of {reveal.figure.ltdTotal}
                                </p>
                            )}
                        </div>
                        <FpButton variant="primary" className="w-full mt-2" onClick={() => setReveal(null)}>Collect</FpButton>
                        <p className="text-[11px] text-gray-400 text-center">
                            It goes straight into your collection - unbox or sell it any time from <strong>My Collection</strong>.
                        </p>
                    </div>
                </FpModal>
            )}
        </FrankoPopLayout>
    );
}
